import { Dropdown, DropdownProps } from "antd";
import { CSSProperties, ReactNode, useMemo, useState } from "react";
import SearchBoxContainer from "../Containers/SearchBoxContainer";
import { Colors } from "../../utils/colors";
import { EmptySearchState } from "../EmptyStates/EmptySearchState";

type DropdownWithSearchProps = {
  items: any[];
  children: ReactNode;
  trigger?: DropdownProps["trigger"];
  placement?: DropdownProps["placement"];
  isOpen: boolean;
  onOpenChange: (
    open: boolean,
    info?: {
      source: "trigger" | "menu";
    }
  ) => void;
  onItemPress: (item: any) => void;
  renderItem: (item: any) => ReactNode;
  renderSubHeader?: ReactNode;
  getSearchableText: (item: any) => string;
  overlayStyle?: CSSProperties;
};

const DropdownWithSearch = (props: DropdownWithSearchProps) => {
  const {
    items,
    children,
    trigger = ["click"],
    placement,
    isOpen,
    onOpenChange,
    onItemPress,
    renderItem,
    renderSubHeader,
    getSearchableText,
    overlayStyle,
  } = props;
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return items;
    }
    return items.filter((item) => {
      const searchableText = getSearchableText(item).toLowerCase();
      return searchableText.includes(normalizedSearch);
    });
  }, [getSearchableText, items, searchTerm]);

  return (
    <Dropdown
      trigger={trigger}
      placement={placement}
      open={isOpen}
      onOpenChange={(nextOpen, info) =>
        onOpenChange(nextOpen, { source: info?.source ?? "trigger" })
      }
      overlayStyle={{ paddingTop: 7, ...overlayStyle }}
      dropdownRender={() => (
        <div
          className="ColumnNormal"
          style={{
            backgroundColor: Colors.WHITE,
            border: `0.5px solid ${Colors.GRAY2}`,
            borderRadius: 8,
            overflow: "hidden",
            gap: 10,
            paddingInline: 10,
            width: 260,
          }}
        >
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <SearchBoxContainer
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={"Search"}
              containerStyles={{
                marginBottom: 0,
                border: `0.5px solid ${Colors.GRAY2}`,
                backgroundColor: Colors.WHITE1,
              }}
              inputStyles={{ color: Colors.BLACK }}
            />
            {renderSubHeader || null}
          </div>
          <div
            className="ColumnNormal"
            style={{
              minHeight: 200,
              maxHeight: 280,
              overflowY: "scroll",
              paddingBottom: 8,
              gap: 4,
            }}
          >
            {filteredItems.length ? (
              filteredItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    onItemPress(item);
                    onOpenChange(false, { source: "trigger" });
                  }}
                  className="AlignedRowSelect"
                  style={{
                    gap: 10,
                    paddingBlock: 7,
                    borderRadius: 8,
                    border: "none",
                    textAlign: "left",
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = Colors.GRAY6;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {renderItem(item)}
                </button>
              ))
            ) : (
              <EmptySearchState
                mainText={"No results found"}
                altItemColor={Colors.GRAY6}
                altTextColor={Colors.GRAY1}
              />
            )}
          </div>
        </div>
      )}
    >
      <div style={{ cursor: "pointer" }}>{children}</div>
    </Dropdown>
  );
};

export default DropdownWithSearch;
